// src/auth/AuthProvider.tsx
 
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { getAuth, onIdTokenChanged, setPersistence, browserLocalPersistence, User, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, sendPasswordResetEmail, reload, Unsubscribe } from "firebase/auth";
import { app, db, functions } from "../firebase"; // must export initialized Firebase app
import { IDLE_RESET_STORAGE_KEY } from "../lib/idle";
import { httpsCallable } from "firebase/functions";

// Helios Forensic Protocol - State Telemetry
import { logStateChange, trackComponentRender } from "../lib/telemetry";

// Observability - Analytics, Error Monitoring, Performance
import { initObservability, setObservabilityUser } from "../services/observabilityInit";

const resolveTestVisibility = (): boolean => {
  if (typeof window !== "undefined") {
    const maybeCypress = (window as unknown as { Cypress?: unknown }).Cypress;
    if (maybeCypress) {
      return true;
    }
    try {
      if (typeof navigator !== "undefined") {
        const ua = navigator.userAgent || "";
        if (/cypress/i.test(ua)) {
          return true;
        }
      }
    } catch {
      // ignore user agent inspection errors
    }
  }

  try {
    const metaEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    if (metaEnv?.MODE === "test" || metaEnv?.VITE_E2E === "1") {
      return true;
    }
  } catch {
    // ignore import.meta lookup issues
  }

  try {
    const maybeProcess = (typeof globalThis !== "undefined" ? (globalThis as unknown as { process?: { env?: Record<string, string | undefined> } }) : undefined)?.process;
    if (maybeProcess?.env?.NODE_ENV === "test") {
      return true;
    }
  } catch {
    // ignore globalThis inspection issues
  }

  return false;
};

const __TEST_VISIBLE__ = resolveTestVisibility();

// P3 DIAGNOSTIC: Log test visibility at module init (removable post-P3)
if (typeof window !== 'undefined') {
  console.info("🔍 [P3-AuthProvider-ModuleInit] Test visibility resolved", {
    __TEST_VISIBLE__,
    hasWindowCypress: typeof (window as unknown as { Cypress?: unknown }).Cypress !== 'undefined',
    // Do NOT log Cypress object itself - it has circular references that crash JSON stringification
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unavailable',
    timestamp: new Date().toISOString()
  });
}

// Helper to detect Cypress runtime (for production smoke tests)
const isCypressRuntime = (): boolean => {
  return typeof window !== 'undefined' && typeof (window as unknown as { Cypress?: unknown }).Cypress !== 'undefined';
};

function signalLoggedOut() {
  if (typeof window === "undefined") {
    return;
  }
  const timestamp = Date.now();
  try {
    window.localStorage.setItem("auth:lastLogoutTs", String(timestamp));
  } catch {
    // ignore storage write failures
  }

  if (!__TEST_VISIBLE__) {
    return;
  }

  try {
    (window as unknown as { __appLastLogoutSignalTs?: number }).__appLastLogoutSignalTs = timestamp;
  } catch {
    // ignore diagnostics failures
  }

  try {
    window.dispatchEvent(new CustomEvent("app:logged-out"));
  } catch {
    // ignore dispatch errors
  }
}

type AuthStatus = "loading" | "signedOut" | "signedIn";

const IDLE_TIMEOUT_OVERRIDE_STORAGE_KEY = "app.auth.idleTimeoutMinutes";
const DEFAULT_IDLE_TIMEOUT_MINUTES = 30;

const readIdleTimeoutMinutes = (): number => {
  let minutes = DEFAULT_IDLE_TIMEOUT_MINUTES;

  try {
    const envMinutes = (import.meta as unknown as { env?: { VITE_AUTH_IDLE_TIMEOUT_MINUTES?: string } }).env?.VITE_AUTH_IDLE_TIMEOUT_MINUTES;
    if (envMinutes) {
      const parsed = Number.parseFloat(envMinutes);
      if (!Number.isNaN(parsed) && parsed > 0) {
        minutes = parsed;
      }
    }
  } catch {
    // ignore
  }

  if (typeof window !== "undefined") {
    try {
      const override = window.localStorage.getItem(IDLE_TIMEOUT_OVERRIDE_STORAGE_KEY);
      if (override) {
        const parsed = Number.parseFloat(override);
        if (!Number.isNaN(parsed) && parsed > 0) {
          minutes = parsed;
        }
      }
    } catch {
      // ignore read failures (private mode, etc.)
    }
  }

  return minutes;
};

// Split contexts for performance: state vs actions
type AuthState = {
  user: User | null;
  loading: boolean;
  initializing: boolean;
  status: AuthStatus;
  authSetupError: string | null;
  claims?: {
    role?: 'super' | 'support';
    isPro?: boolean;
    admin?: boolean;
    super?: boolean;
    // Demo/QA claims (Phase 2: Entitlements System)
    demo?: boolean;
    qa?: boolean;
    demoTokens?: number;
    // Audit bypass claim (emulator only)
    auditBypassBilling?: boolean;
  };
};

type AuthActions = {
  signUp: (email: string, password: string, displayName?: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOutUser: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  sendEmailVerificationIfNeeded: () => Promise<void>;
  reloadUser: () => Promise<void>;
  /**
   * Force-refresh the Firebase ID token and update AuthProvider's claims state.
   * Call this after any out-of-band token refresh (e.g. after onLoginSetClaims)
   * so that guards like RequireProAccess see the fresh claims immediately.
   */
  refreshClaims: () => Promise<void>;
};

// State context - changes frequently
const AuthStateContext = createContext<AuthState>({
  user: null,
  loading: true,
  initializing: true,
  status: "loading",
  authSetupError: null,
  claims: undefined,
});

// Actions context - stable (never changes)
const AuthActionsContext = createContext<AuthActions>({
  signUp: async () => {},
  signIn: async () => {},
  signOutUser: async () => {},
  sendPasswordReset: async () => {},
  sendEmailVerificationIfNeeded: async () => {},
  reloadUser: async () => {},
  refreshClaims: async () => {},
});

// Legacy combined context for backward compatibility
type AuthCtx = AuthState & AuthActions;
const Ctx = createContext<AuthCtx>({
  user: null, 
  loading: true, 
  initializing: true,
  status: "loading",
  authSetupError: null,
  claims: undefined,
  signUp: async () => {},
  signIn: async () => {},
  signOutUser: async () => {},
  sendPasswordReset: async () => {},
  sendEmailVerificationIfNeeded: async () => {},
  reloadUser: async () => {},
  refreshClaims: async () => {},
});

const resolveTestSignalLoggedOut = (): (() => void) | undefined => {
  try {
    const maybeProcess = (typeof globalThis !== "undefined" ? (globalThis as unknown as { process?: { env?: Record<string, string | undefined> } }) : undefined)?.process;
    if (maybeProcess?.env?.NODE_ENV === "test") {
      return () => signalLoggedOut();
    }
  } catch {
    // ignore resolution errors in non-test runtimes
  }
  return undefined;
};

export const __testSignalLoggedOut = resolveTestSignalLoggedOut();

export const AuthProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [user, setUser] = useState<User|null>(null);
  const [claims, setClaims] = useState<{ role?: 'super' | 'support' } | undefined>(undefined);
  const [_status, _setStatus] = useState<AuthStatus>("loading");
  const [authSetupError, setAuthSetupError] = useState<string | null>(null);
  const auth = getAuth(app);
  
  // Session flags to prevent repeated auth function calls (TASK D optimization)
  const authBootstrapCompleted = useRef<{ 
    uid: string | null;
    loggedEvent: boolean;
    ensuredDoc: boolean;
    setClaims: boolean;
  }>({ uid: null, loggedEvent: false, ensuredDoc: false, setClaims: false });

  // Ref to track latest status synchronously (prevents stale closures in timer)
  const statusRef = useRef<AuthStatus>(_status);
  
  // Stable setter that updates both state and ref synchronously
  const setStatusRefAndState = useCallback((value: AuthStatus) => {
    // Runtime Cypress detection for production smoke tests
    const shouldLog = isCypressRuntime() || import.meta.env.DEV;
    
    if (shouldLog) {
      console.info(`🔧 [AuthProvider] setStatusRefAndState called:`, {
        oldStatus: statusRef.current,
        newStatus: value,
        timestamp: new Date().toISOString()
      });
    }
    statusRef.current = value;
    _setStatus(value);
    if (shouldLog) {
      console.info(`🔧 [AuthProvider] statusRef updated:`, {
        statusRef_current: statusRef.current,
        timestamp: new Date().toISOString()
      });
    }
  }, []);
  
  const status = _status; // Alias for readability

  // Stable background task wrappers (fire-and-forget)
  // Step 2: Make bootstrap best-effort and timeboxed (non-blocking)
  const fireLogAuthEvent = useCallback((firebaseUser: User) => {
    if (typeof window !== 'undefined') {
      window.__TL_BOOT_STEP__ = 'logAuthEvent_started';
    }
    
    const timeout = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('logAuthEvent timeout')), 2000)
    );
    
    Promise.race([
      httpsCallable(functions, 'logAuthEvent')({
        eventType: 'login',
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
        metadata: { 
          source: 'auth_state_change',
          email: firebaseUser.email || 'unknown'
        }
      }),
      timeout
    ])
      .then(() => {
        if (typeof window !== 'undefined') window.__TL_BOOT_STEP__ = 'logAuthEvent_done';
      })
      .catch((err) => {
        console.warn("[Auth] Non-critical: Failed to log login event", err.message);
        if (typeof window !== 'undefined') window.__TL_BOOT_STEP__ = 'logAuthEvent_failed';
      });
  }, []);

  const fireEnsureUserDoc = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.__TL_BOOT_STEP__ = 'ensureUserDoc_started';
    }
    
    const timeout = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('ensureUserDoc timeout')), 2500)
    );
    
    Promise.race([
      httpsCallable(functions, 'ensureUserDoc')({}),
      timeout
    ])
      .then(() => {
        setAuthSetupError(null);
        if (typeof window !== 'undefined') window.__TL_BOOT_STEP__ = 'ensureUserDoc_done';
      })
      .catch((err) => {
        console.warn("[Auth] Non-critical: Failed to ensure user document", err.message);
        if (typeof window !== 'undefined') window.__TL_BOOT_STEP__ = 'ensureUserDoc_failed';
        // Don't block - show banner in UI but allow navigation
      });
  }, []);

  const fireOnLoginSetClaims = useCallback((firebaseUser: User) => {
    if (typeof window !== 'undefined') {
      window.__TL_BOOT_STEP__ = 'onLoginSetClaims_started';
    }
    
    const timeout = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('onLoginSetClaims timeout')), 2500)
    );
    
    Promise.race([
      httpsCallable(functions, 'onLoginSetClaims')({}),
      timeout
    ])
      .then(async (result) => {
        // Type guard: timeout rejects, so result must be from httpsCallable
        const data = (result as { data?: { isAdmin?: boolean; hasPro?: boolean } })?.data;
        if (import.meta.env.DEV) console.info("[Auth] Claims synced:", data);
        // Refresh token and update local claims for BOTH admin and pro users.
        // Previously only admins triggered a refresh, leaving pro users with stale
        // claims in the AuthProvider context after onLoginSetClaims set isPro:true.
        // Always refresh and set claims after onLoginSetClaims completes.
        // For admin/pro users: force-refresh picks up newly minted custom claims.
        // For SRL users with no custom claims: sets claims to the (possibly empty)
        // token result, completing the bootstrap and ending the undefined/spinner
        // state that guards use to detect a pending bootstrap sequence.
        if (data?.isAdmin || data?.hasPro) {
          // Force-refresh required to pick up custom claims written by the function.
          await firebaseUser.getIdToken(true);
        }
        const idTokenResult = await firebaseUser.getIdTokenResult();
        const customClaims = idTokenResult.claims as { 
          role?: 'super' | 'support';
          isPro?: boolean;
          admin?: boolean;
          auditBypassBilling?: boolean;
          isTestUser?: boolean;
        };
        setClaims(customClaims);
        if (typeof window !== 'undefined') window.__TL_BOOT_STEP__ = 'onLoginSetClaims_done';
      })
      .catch(async (err) => {
        console.warn("[Auth] Non-critical: Failed to sync admin claims", err.message);
        if (typeof window !== 'undefined') window.__TL_BOOT_STEP__ = 'onLoginSetClaims_failed';
        // CRITICAL: Even on timeout, we must call setClaims so the bootstrap spinner
        // is unblocked. Without this, users with slow cold-start functions hang forever
        // on /pro/register and any other page guarded by the claims sentinel.
        // Fall back to the cached token claims — if the user was previously bootstrapped
        // as Pro (token has isPro: true), they'll be routed correctly.
        try {
          const idTokenResult = await firebaseUser.getIdTokenResult();
          const cachedClaims = idTokenResult.claims as {
            role?: 'super' | 'support';
            isPro?: boolean;
            admin?: boolean;
            auditBypassBilling?: boolean;
            isTestUser?: boolean;
          };
          setClaims(cachedClaims);
        } catch {
          setClaims({}); // Last-resort: unblock spinner, user routes as SRL
        }
      });

    // PRO-CONFLICTS-WO: Log pro auth for debugging
    if (import.meta.env.DEV) {
      firebaseUser.getIdTokenResult().then(tokenResult => {
        console.debug('[PRO-CONFLICTS-WO] Pro auth user:', {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          claims: {
            role: tokenResult.claims.role,
            admin: tokenResult.claims.admin
          }
        });
      }).catch(err => console.warn('[PRO-CONFLICTS-WO] Could not fetch token:', err));
    }
  }, []);

  // HELIOS: Track AuthProvider renders
  useEffect(() => {
    trackComponentRender('AuthProvider', { status, hasUser: !!user });
  }, [status, user]); // Only track when status or user changes

  // HELIOS: Log state changes
  useEffect(() => {
    logStateChange('AuthProvider', {
      user: user ? { uid: user.uid, email: user.email, emailVerified: user.emailVerified } : null,
      status,
      loading: status === 'loading',
      authSetupError
    });
  }, [user, status, authSetupError]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    // Set __appAuthStatus for test visibility (build-time check) OR Cypress runtime detection
    if (!__TEST_VISIBLE__ && !isCypressRuntime()) {
      return;
    }

    (window as unknown as { __appAuthStatus?: AuthStatus }).__appAuthStatus = status;
    
    // Set __appAuthUid for test precondition checks
    if (status === 'signedIn' && user?.uid) {
      (window as unknown as { __appAuthUid?: string }).__appAuthUid = user.uid;
    } else {
      delete (window as unknown as { __appAuthUid?: string }).__appAuthUid;
    }

    return () => {
      if (typeof window === "undefined") {
        return;
      }
      delete (window as unknown as { __appAuthStatus?: AuthStatus }).__appAuthStatus;
      delete (window as unknown as { __appAuthUid?: string }).__appAuthUid;
    };
  }, [status, user]);

  // Stuck loading timer - only logs if auth is still loading after 10s
  useEffect(() => {
    if (status !== 'loading') return;

    const startedAt = Date.now();
    const userSnapshot = user; // Capture user at timer start
    
    const timeoutId = window.setTimeout(() => {
      // Check ref for current status (not captured closure value)
      const currentStatus = statusRef.current;
      
      // Runtime Cypress detection for production smoke tests
      const shouldLog = isCypressRuntime() || import.meta.env.DEV;
      
      if (shouldLog) {
        console.info("🕐 [AuthProvider] Timer fired after 10s:", {
          statusRef_current: statusRef.current,
          currentStatus: currentStatus,
          elapsedMs: Date.now() - startedAt,
          timestamp: new Date().toISOString()
        });
      }
      
      if (currentStatus !== 'loading') {
        // Auth resolved before timeout; do not log stuck warning
        if (shouldLog) {
          console.info("✅ [AuthProvider] Timer check passed - status resolved");
        }
        return;
      }

      console.warn(
        "⚠️ [STUCK LOADING] AuthProvider still loading after 10s",
        {
          status: currentStatus,
          user: userSnapshot ? { uid: userSnapshot.uid, email: userSnapshot.email } : null,
          elapsedMs: Date.now() - startedAt,
          timestamp: new Date().toISOString(),
          isCypressRuntime: isCypressRuntime(),
          firebaseAuth: auth ? {
            hasCurrentUser: !!auth.config, // Check config instead of currentUser to avoid ESLint violation
            projectId: auth.config?.apiKey ? 'configured' : 'missing'
          } : 'not initialized'
        }
      );
    }, 10_000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [status, user, auth]);

  useEffect(() => {
    let unsub: Unsubscribe | null = null;
    let cancelled = false;

    (async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
      } catch (err) {
        // Persistence can fail in private browsing; proceed with default session.
        console.warn("[Auth] Failed to set browser persistence", err);
      }

      if (cancelled) {
        return;
      }

      // Initialize observability services once (safe to call multiple times)
      initObservability().catch((err) => {
        console.warn("[Auth] Failed to init observability", err);
      });

      // Runtime Cypress detection for production smoke tests
      if (isCypressRuntime() || import.meta.env.DEV) {
        console.info("🔧 [AuthProvider] Registering onIdTokenChanged listener", {
          isCypressRuntime: isCypressRuntime(),
          isDev: import.meta.env.DEV,
          timestamp: new Date().toISOString()
        });
      }

      unsub = onIdTokenChanged(auth, async (firebaseUser) => {
        const nextStatus: AuthStatus = firebaseUser ? "signedIn" : "signedOut";
        
        // Runtime Cypress detection for production smoke tests (declare once for entire callback)
        const shouldLog = isCypressRuntime() || import.meta.env.DEV || import.meta.env.VITE_DEBUG_LOGIN === "true";
        
        if (shouldLog) {
          console.info("🔄 [AuthProvider] Firebase auth state change", {
            status: nextStatus,
            hasUser: !!firebaseUser,
            email: firebaseUser?.email || null,
            uid: firebaseUser?.uid?.slice(0, 8) || null,
            timestamp: new Date().toISOString()
          });
        }
        
        
        // CRITICAL FIX: Update user and status IMMEDIATELY (before any async checks)
        // This ensures LoginPage re-renders and can navigate, even if deactivation check hangs
        setUser(firebaseUser);
        setStatusRefAndState(nextStatus);
        
        // HELIOS: Set auth ready signal for test determinism (first onIdTokenChanged callback)
        // Check window.Cypress directly (not via isCypressRuntime) since Cypress injects it late
        const isCypress = typeof window !== 'undefined' && typeof (window as unknown as { Cypress?: unknown }).Cypress !== 'undefined';
        
        // P3 DIAGNOSTIC: Always log test visibility detection (removable post-P3)
        if (typeof window !== 'undefined' && (isCypress || shouldLog)) {
          console.info("🔍 [P3-AuthProvider] Test visibility check", {
            __TEST_VISIBLE__,
            isCypress,
            hasWindowCypress: typeof (window as unknown as { Cypress?: unknown }).Cypress !== 'undefined',
            willSetReadySignal: __TEST_VISIBLE__ || isCypress,
            timestamp: new Date().toISOString()
          });
        }
        
        if (typeof window !== 'undefined' && (__TEST_VISIBLE__ || isCypress)) {
          // Signal to tests that auth state has been determined (whether signed in or out)
          // Set beacon in format Cypress expects: { ts: timestamp }
          (window as unknown as { __TL_AUTH_READY__?: { ts: number } }).__TL_AUTH_READY__ = { ts: Date.now() };
          (window as unknown as { __TL_AUTH_UID__?: string | null }).__TL_AUTH_UID__ = firebaseUser?.uid ?? null;
        } else if (typeof window !== 'undefined' && (isCypress || shouldLog)) {
          // P3 DIAGNOSTIC: Log when signal is NOT set (removable post-P3)
          console.warn("⚠️  [P3-AuthProvider] Auth ready signal NOT set", {
            __TEST_VISIBLE__,
            isCypress,
            hasWindowCypress: typeof (window as unknown as { Cypress?: unknown }).Cypress !== 'undefined',
            reason: !__TEST_VISIBLE__ && !isCypress ? "Neither __TEST_VISIBLE__ nor isCypress is true" : "unknown",
            timestamp: new Date().toISOString()
          });
        }
        
        // Check if user account is deactivated (async, non-blocking)
        if (firebaseUser) {
          (async () => {
            try {
              const { doc, getDoc } = await import('firebase/firestore');
              const userDocRef = doc(db, 'users', firebaseUser.uid);
              const userDocSnap = await getDoc(userDocRef);
              
              if (userDocSnap.exists()) {
                const userData = userDocSnap.data();
                
                if (userData.status === 'deactivated') {
                  console.warn("🚫 [AuthProvider] User account is deactivated, signing out", {
                    uid: firebaseUser.uid,
                    email: firebaseUser.email,
                    timestamp: new Date().toISOString()
                  });
                  
                  // Sign out immediately
                  await signOut(auth);
                  setUser(null);
                  setStatusRefAndState("signedOut");
                  setClaims(undefined);
                  setObservabilityUser(null);
                  signalLoggedOut();
                  
                  // Show error message to user
                  const toast = await import('react-hot-toast');
                  toast.default.error('This account has been deactivated. Please contact support.');
                }
              }
            } catch (error) {
              console.error("[Auth] Failed to check user status:", error);
              // Continue with login even if status check fails
            }
          })();
        }
        
        // Fetch custom claims if user is signed in
        if (firebaseUser) {
          try {
            const idTokenResult = await firebaseUser.getIdTokenResult();
            const customClaims = idTokenResult.claims as { 
              role?: 'super' | 'support';
              isPro?: boolean;
              admin?: boolean;
              auditBypassBilling?: boolean;
              isTestUser?: boolean;
            };

            // Guard: only set claims if the token already contains ThreadLock-specific
            // custom claims. On the FIRST onIdTokenChanged fire after sign-in,
            // onLoginSetClaims has not yet run and the token has no custom claims.
            // Calling setClaims({}) here destroys the `undefined` sentinel that guards
            // rely on to stay in the bootstrap spinner, causing SRL users to redirect
            // to /cases/new before their real claims are available.
            // When hasThreadLockClaims is false we leave claims as undefined;
            // fireOnLoginSetClaims will call setClaims() once the bootstrap completes.
            const rawClaims = idTokenResult.claims as Record<string, unknown>;
            const hasThreadLockClaims = !!(rawClaims.isPro || rawClaims.hasPro || rawClaims.pro || rawClaims.admin || rawClaims.demo);

            if (hasThreadLockClaims) {
              setClaims(customClaims);
            }
            // else: no ThreadLock claims yet — leave claims as undefined
            // so guards hold on the bootstrap spinner.

            // Set user context for observability
            setObservabilityUser(firebaseUser.uid, {
              email: firebaseUser.email || undefined,
              role: customClaims?.role,
            });
          } catch (err) {
            console.warn("[Auth] Failed to fetch custom claims", err);
            setClaims(undefined);
            
            // Set user context even if claims fetch failed
            setObservabilityUser(firebaseUser.uid, {
              email: firebaseUser.email || undefined,
            });
          }
        } else {
          setClaims(undefined);
          
          // Clear user context on logout
          setObservabilityUser(null);
        }
        
        // Fire background tasks (non-blocking, fire-and-forget)
        if (firebaseUser) {
          if (typeof window !== 'undefined') {
            window.__TL_BOOT_STEP__ = 'auth_bootstrap_started';
          }
          
          const currentUid = firebaseUser.uid;
          const bootstrap = authBootstrapCompleted.current;
          
          // Reset flags if user changed (new login)
          if (bootstrap.uid !== currentUid) {
            bootstrap.uid = currentUid;
            bootstrap.loggedEvent = false;
            bootstrap.ensuredDoc = false;
            bootstrap.setClaims = false;
          }
          
          // ✅ HELIOS FIX (2026-02-08): Re-enable bootstrap functions - they ARE deployed and have timeouts
          // These functions are ESSENTIAL: without them, user docs and billing data are never created
          // The timeouts (2-2.5s) ensure they don't block login even if slow
          // Only call each function once per user session
          if (!bootstrap.loggedEvent) {
            fireLogAuthEvent(firebaseUser);
            bootstrap.loggedEvent = true;
          }
          if (!bootstrap.ensuredDoc) {
            fireEnsureUserDoc();
            bootstrap.ensuredDoc = true;
          }
          if (!bootstrap.setClaims) {
            fireOnLoginSetClaims(firebaseUser);
            bootstrap.setClaims = true;
          }
          console.info('[AuthProvider] ✅ Bootstrap functions RE-ENABLED - user/billing setup active');
        } else {
          // Reset flags on logout
          authBootstrapCompleted.current = { uid: null, loggedEvent: false, ensuredDoc: false, setClaims: false };
          signalLoggedOut();
        }
        
        if (shouldLog) {
          console.info("✅ [AuthProvider] State transition complete", {
            from: "loading",
            to: nextStatus,
            timestamp: new Date().toISOString()
          });
        }
      });
    })();

    return () => {
      cancelled = true;
      if (unsub) {
        unsub();
      }
    };
  }, [auth, setUser, setStatusRefAndState, fireLogAuthEvent, fireEnsureUserDoc, fireOnLoginSetClaims]);

  useEffect(() => {
    if (typeof window === "undefined" || status !== "signedIn") {
      return () => {};
    }

    let idleTimer: ReturnType<typeof setTimeout> | null = null;

    const idleMinRef = { current: DEFAULT_IDLE_TIMEOUT_MINUTES };
    if (__TEST_VISIBLE__) {
      (window as unknown as { __appIdleMinMinutes?: { current: number } }).__appIdleMinMinutes = idleMinRef;
    }

    const runIdleSignOut = async () => {
      try {
        await auth.signOut();
        signalLoggedOut();
      } catch (err) {
        console.error("[Auth] Idle sign-out failed", err);
      }
    };

    // [P3] Track how many times idle callback fires for deterministic testing
    const idleFiresCounter = { current: 0 };
    let idleDeadlineEpochMs = 0;

    function scheduleIdleSignOut() {
      if (idleTimer) {
        clearTimeout(idleTimer);
      }
      const minutes = readIdleTimeoutMinutes();
      const floorMinutesRaw = idleMinRef.current;
      const floorMinutes = Number.isFinite(floorMinutesRaw) && floorMinutesRaw >= 0 ? floorMinutesRaw : DEFAULT_IDLE_TIMEOUT_MINUTES;
      const timeoutMinutes = Math.max(minutes, floorMinutes);
      const timeout = timeoutMinutes * 60 * 1000;
      idleDeadlineEpochMs = Date.now() + timeout;
      if (__TEST_VISIBLE__) {
        (window as unknown as { __appIdleTimeoutMs?: number }).__appIdleTimeoutMs = timeout;
      }
      idleTimer = setTimeout(() => {
        idleFiresCounter.current++;
        void runIdleSignOut();
      }, timeout);
    }

    function broadcastReset() {
      try {
        window.localStorage.setItem(IDLE_RESET_STORAGE_KEY, String(Date.now()));
      } catch {
        // ignore broadcast failures
      }
    }

    function resetFromActivity() {
      scheduleIdleSignOut();
      broadcastReset();
    }

    function resetFromStorage(event: StorageEvent) {
      if (event.storageArea === window.localStorage && event.key === IDLE_RESET_STORAGE_KEY) {
        scheduleIdleSignOut();
      }
    }

    function resetFromVisibility() {
      if (document.visibilityState === "visible") {
        scheduleIdleSignOut();
        broadcastReset();
      }
    }

    const activityEvents: Array<[keyof WindowEventMap, EventListener]> = [
      ["mousemove", resetFromActivity],
      ["keydown", resetFromActivity],
    ];

    // [P3 FIX] E2E idle timer control - check if auto-start should be disabled
    const isE2E = typeof window !== 'undefined' && typeof (window as any).Cypress !== 'undefined';
    const disableAutoStart = isE2E && (window as any).__TL_E2E__?.disableIdleAutoStart === true;
    
    if (!disableAutoStart) {
      scheduleIdleSignOut();
    } else {
      console.log('[P3-E2E] Idle auto-start disabled, waiting for manual startIdleTimer() call');
    }

    if (__TEST_VISIBLE__) {
      (window as unknown as { __appScheduleIdleSignOut?: () => void }).__appScheduleIdleSignOut = scheduleIdleSignOut;
      (window as unknown as { __appForceIdleSignOut?: () => Promise<void> }).__appForceIdleSignOut = runIdleSignOut;
      
      // [P3 FIX] Add E2E hooks for controlled idle timer testing
      if (isE2E && typeof (window as any).__TL_E2E__ === 'object') {
        (window as any).__TL_E2E__.startIdleTimer = () => {
          scheduleIdleSignOut();
          console.log('[P3-E2E] Idle timer started manually');
        };
        
        (window as any).__TL_E2E__.setIdleMinutes = (minutes: number) => {
          idleMinRef.current = minutes;
          window.localStorage.setItem('app.auth.idleTimeoutMinutes', String(minutes));
          console.log('[P3-E2E] Idle timeout set to', minutes, 'minutes');
        };
        
        // [P3] Comprehensive debug hooks for surgical E2E testing
        (window as any).__TL_E2E__.idleDebug = () => ({
          enabled: true,
          idleMinutes: readIdleTimeoutMinutes(),
          floorMinutes: idleMinRef.current,
          deadlineEpochMs: idleDeadlineEpochMs,
          timerKind: 'setTimeout',
          timerId: idleTimer !== null ? 'active' : 'none',
          lastActivityEpochMs: Date.now(),
          fires: idleFiresCounter.current,
        });
        
        (window as any).__TL_E2E__.forceIdleNow = () => {
          void runIdleSignOut();
        };
        
        // E2E test hook - uses context user to comply with auth pattern enforcement
        (window as any).__TL_E2E__.currentUid = () => user?.uid ?? null;
      }
    }

    activityEvents.forEach(([eventName, handler]) => {
      window.addEventListener(eventName, handler, { passive: true });
    });
    window.addEventListener("storage", resetFromStorage);
    document.addEventListener("visibilitychange", resetFromVisibility);

    return () => {
      if (idleTimer) {
        clearTimeout(idleTimer);
      }
      activityEvents.forEach(([eventName, handler]) => {
        window.removeEventListener(eventName, handler);
      });
      window.removeEventListener("storage", resetFromStorage);
      document.removeEventListener("visibilitychange", resetFromVisibility);
      if (__TEST_VISIBLE__) {
        delete (window as unknown as { __appScheduleIdleSignOut?: () => void }).__appScheduleIdleSignOut;
        delete (window as unknown as { __appForceIdleSignOut?: () => Promise<void> }).__appForceIdleSignOut;
        delete (window as unknown as { __appIdleTimeoutMs?: number }).__appIdleTimeoutMs;
        delete (window as unknown as { __appIdleMinMinutes?: { current: number } }).__appIdleMinMinutes;
      }
    };
  }, [auth, status]);

  const signUp = useCallback(async (email: string, password: string, displayName?: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // If display name provided, update user profile
    if (displayName) {
      const { updateProfile } = await import('firebase/auth');
      await updateProfile(userCredential.user, { displayName });
      
      // Also store in Firestore users collection for conflict checks
      const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
      
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        uid: userCredential.user.uid,
        email: email,
        displayName: displayName,
        createdAt: serverTimestamp(),
      }, { merge: true });
    }

    // Send verification email immediately after account creation.
    // New trial accounts are never Pro (isPro: false).
    // forceClaimRefresh: false — brand-new user has no claims yet.
    try {
      const { sendVerificationEmail } = await import('@/lib/emailVerification');
      await sendVerificationEmail({
        user: userCredential.user,
        isPro: false,
        forceClaimRefresh: false,
      });
    } catch (emailErr) {
      // Non-blocking — user can resend from the /verify-email page
      console.warn('[AuthProvider] signUp: failed to send verification email', emailErr);
    }
  }, [auth]);

  const signIn = useCallback(async (email: string, password: string) => {
    const DEBUG = import.meta.env.VITE_DEBUG_LOGIN === "true";
    if (DEBUG) console.log("[AuthProvider.signIn] START:", email.slice(0, 3) + "***");
    await signInWithEmailAndPassword(auth, email, password);
    if (DEBUG) console.log("[AuthProvider.signIn] COMPLETE");
  }, [auth]);

  const signOutUser = useCallback(async () => {
    // Clear auth-related state that could cause redirect loops
    try {
      // Clear any stored redirect paths or case pointers
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem('currentCaseId');
        window.localStorage.removeItem('postAuthRedirect');
        window.localStorage.removeItem('pendingRedirect');
        window.localStorage.removeItem('authSetupError');
      }
      
      // Clear session storage as well
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.removeItem('currentCaseId');
        window.sessionStorage.removeItem('postAuthRedirect');
        window.sessionStorage.removeItem('pendingRedirect');
        window.sessionStorage.removeItem('authSetupError');
      }
    } catch (error) {
      console.warn('[AuthProvider] Failed to clear storage during sign out:', error);
    }
    
    await signOut(auth);
    signalLoggedOut();
  }, [auth]);

  const sendPasswordReset = useCallback(async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  }, [auth]);

  const sendEmailVerificationIfNeeded = useCallback(async () => {
    if (user && !user.emailVerified) {
      console.info('[AuthProvider] sendEmailVerificationIfNeeded called for:', user.email);
      
      try {
        // Use centralized email verification service
        const { sendVerificationEmail, detectAccountType } = await import('@/lib/emailVerification');
        const isPro = await detectAccountType(user);
        await sendVerificationEmail({ user, isPro });
      } catch (error: any) {
        console.error('[AuthProvider] Failed to send verification email:', error);
        throw error;
      }
    }
  }, [user]);

  const reloadUser = useCallback(async () => {
    if (user) {
      await reload(user);
      // Force a re-render by updating user state with functional update
      setUser(u => u ? { ...u } : null);
    }
  }, [user]);

  // Refresh Firebase ID token and sync claims into AuthProvider state.
  // Required because AuthProvider now uses onIdTokenChanged (fires on token refresh too),
  // so out-of-band token refreshes (e.g. after onLoginSetClaims) are visible to it.
  // Guards like RequireProAccess read claims from this context, so they must be
  // updated before any navigation happens.
  //
  // IMPORTANT: Uses getAuth().currentUser (not the state `user`) to avoid a stale
  // closure bug. When ProLoginPage captures this function at render-time the state
  // `user` is null (not yet signed in). Calling getAuth().currentUser inside the
  // function body always reads the live Firebase Auth state, so the force-refresh
  // succeeds even though the closure was captured before sign-in.
  const refreshClaims = useCallback(async () => {
    const currentUser = getAuth().currentUser;
    if (!currentUser) return;
    try {
      // Force-refresh=true ensures we get the token written by onLoginSetClaims,
      // not a locally-cached version that may predate the claims update.
      const idTokenResult = await currentUser.getIdTokenResult(true);
      setClaims(idTokenResult.claims as {
        role?: 'super' | 'support';
        isPro?: boolean;
        admin?: boolean;
        super?: boolean;
        demo?: boolean;
        qa?: boolean;
        demoTokens?: number;
        auditBypassBilling?: boolean;
      });
      if (import.meta.env.DEV) {
        console.info('[AuthProvider] refreshClaims complete', {
          claimsUpdated: true,
        });
      }
    } catch (err) {
      console.warn('[AuthProvider] refreshClaims failed', err);
    }
  }, []); // no deps: reads live getAuth().currentUser, not stale state `user`

  // Actions object - memoized with empty deps (stable)
  const actions = useMemo<AuthActions>(() => ({
    signUp,
    signIn,
    signOutUser,
    sendPasswordReset,
    sendEmailVerificationIfNeeded,
    reloadUser,
    refreshClaims,
  }), [signUp, signIn, signOutUser, sendPasswordReset, sendEmailVerificationIfNeeded, reloadUser, refreshClaims]);

  // State object - memoized, changes when user/status changes
  const state = useMemo<AuthState>(() => ({
    user,
    loading: status === "loading",
    initializing: status === "loading",
    status,
    authSetupError,
    claims,
  }), [user, status, authSetupError, claims]);

  // Legacy combined value for backward compatibility
  const value = useMemo(() => ({ 
    ...state,
    ...actions,
  }), [state, actions]);

  // Render blocking error UI if critical auth setup fails
  if (authSetupError) {
    return (
      <div className="flex items-center justify-center h-screen bg-brand-navy text-white p-8">
        <div className="max-w-md text-center bg-slate-800 p-8 rounded-lg border border-brand-orange">
          <div className="text-5xl mb-4">⚠️</div>
          <h2 className="text-brand-orange text-2xl font-bold mb-4">Authentication Error</h2>
          <p className="mb-6 leading-relaxed text-slate-300">{authSetupError}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-brand-orange hover:bg-orange-600 text-white font-bold py-3 px-6 rounded-md transition-colors"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <AuthActionsContext.Provider value={actions}>
      <AuthStateContext.Provider value={state}>
        <Ctx.Provider value={value}>{children}</Ctx.Provider>
      </AuthStateContext.Provider>
    </AuthActionsContext.Provider>
  );
};
// Optimized hooks - use these for best performance
export const useAuthState = () => useContext(AuthStateContext);
export const useAuthActions = () => useContext(AuthActionsContext);

// Legacy hooks for backward compatibility
export const useAuthCtx = () => useContext(Ctx);
export const useAuth = () => useContext(Ctx);

/**
 * Admin Role Check Hook
 * DIRECTIVE 3: Maps isAdmin() requirement to existing claim.role == 'super'
 * 
 * Returns true if user is a Super Admin with full platform access.
 * Support admins (role: 'support') are read-only and NOT considered "admin" for write operations.
 * 
 * Usage:
 * ```tsx
 * const isAdmin = useAdmin();
 * if (isAdmin) {
 *   // Show admin controls, allow create/update/delete
 * }
 * ```
 */
export const useAdmin = (): boolean => {
  const { claims } = useAuth();
  return claims?.role === 'super';
};

/**
 * Support Admin Check Hook
 * Returns true if user is a Support Admin (read-only admin access)
 * 
 * Usage:
 * ```tsx
 * const isSupportAdmin = useSupportAdmin();
 * if (isSupportAdmin) {
 *   // Show read-only admin views
 * }
 * ```
 */
export const useSupportAdmin = (): boolean => {
  const { claims } = useAuth();
  return claims?.role === 'support';
};

/**
 * Any Admin Check Hook
 * Returns true if user has any admin role (super or support)
 * 
 * Usage:
 * ```tsx
 * const isAnyAdmin = useAnyAdmin();
 * if (isAnyAdmin) {
 *   // Show admin navigation
 * }
 * ```
 */
export const useAnyAdmin = (): boolean => {
  const { claims } = useAuth();
  return claims?.role === 'super' || claims?.role === 'support';
};